import { z } from 'zod';

import { appNameForMode } from './app-name';

const envSchema = z.object({
  VITE_SSO_GOOGLE_ENABLED: z
    .string()
    .default('false')
    .transform((v) => v === 'true'),
  VITE_SSO_MICROSOFT_ENABLED: z
    .string()
    .default('false')
    .transform((v) => v === 'true'),
  VITE_SENTRY_DSN: z.string().optional(),
  VITE_COMMIT_HASH: z.string().default('local'),
  // Origin URL of the identity-service backend (Vite proxy target in dev).
  // Surfaced at runtime so the SSO bridge can derive the cloud-FE origin to
  // bounce through when localhost dev points at a remote API.
  VITE_API_BASE_URL: z.string().default(''),
  // Origin URL of the platform-gateway Cloud Run service. Doubles as the
  // Vite proxy target in dev *and* the runtime origin used by endpoints
  // that must bypass Firebase Hosting (notifications SSE — the Hosting CDN
  // buffers `text/event-stream` responses through `run` rewrites).
  VITE_PLATFORM_BASE_URL: z.string().default(''),
  // Origin URL of the ai-mcp-server Cloud Run service. Used by chat /
  // copilot SSE endpoints (`/chat/conversations/:id/messages`) — a separate
  // service from platform-gateway, so it can't share the `/api` prefix. Same
  // reasoning as `VITE_PLATFORM_BASE_URL`: Vite proxy target in dev, runtime
  // origin in prod via the `/mcp-api/**` Firebase Hosting rewrite.
  VITE_MCP_BASE_URL: z.string().default(''),
  // Opt-in switch for the Mock Service Worker. When `'true'` (and only in
  // dev) MSW intercepts fetch and mock-backed code paths render fixtures
  // instead of hitting / failing on real endpoints. Off in staging + prod.
  VITE_MSW: z
    .string()
    .default('false')
    .transform((v) => v === 'true'),
  // Local-only feature flag overrides — comma-separated `key:true/false` pairs.
  // Only applied when `import.meta.env.DEV` is true; ignored in prod builds.
  // Set in `.env.local` (gitignored) so overrides never leave your machine.
  // Example: VITE_DEV_FEATURE_FLAGS=trial-flow:true,experiments:false
  VITE_DEV_FEATURE_FLAGS: z.string().default(''),
});

export type Env = z.infer<typeof envSchema>;

export const ENV: Env = envSchema.parse(import.meta.env);

/**
 * True only when the user explicitly opted into mock mode AND we're running
 * a dev build. Production bundles can never enable mocks even if `VITE_MSW`
 * is somehow set, because `import.meta.env.DEV` is a build-time constant
 * that Vite tree-shakes to `false` for prod builds — the mock branches
 * disappear entirely from the shipped JS.
 */
export const IS_MOCK_MODE: boolean = ENV.VITE_MSW && import.meta.env.DEV;

/**
 * True only for the production build (`vite build --mode production`), not the
 * dev server, `develop`, or `staging` modes. Used to gate production-only
 * behaviour like the post-login service wake-up.
 */
export const IS_PRODUCTION: boolean = import.meta.env.MODE === 'production';

/**
 * Product name for this build ("Lily MAX" in production, "Lily MAX Dev" / "Lily MAX Stage"
 * elsewhere). Shown on the public homepage and login page; must match the Google OAuth
 * consent-screen name for the environment — see `./app-name.ts`.
 */
export const APP_NAME: string = appNameForMode(import.meta.env.MODE);

/**
 * The production front-end origin, from `.firebaserc`'s `production` hosting target
 * (site `lily-agentic`). Deliberately a single host: anything not listed here is treated
 * as non-production.
 */
const PRODUCTION_HOSTNAMES = new Set(['lily-agentic.web.app']);

/**
 * True only when this is a production BUILD **and** it is actually being served from a
 * production host — the gate for behaviour that must never touch a non-production backend,
 * currently the service wake-up (post-login and the periodic warm-up).
 *
 * `IS_PRODUCTION` alone is a build-flag assertion, and this repo has already been bitten
 * by it: a bare `vite build` defaults to mode `production`, so a mis-wired dev or staging
 * workflow silently produced `MODE === 'production'` bundles (see the comments in
 * `.github/workflows/deploy.yml` / `deploy-staging.yml`). That would point the wake-up
 * fan-out at whichever backend the bundle was configured for. The origin check makes the
 * gate depend on where the app is really running, not on a flag someone can mis-set.
 *
 * FAILS CLOSED: an unrecognised host gets no wake-up. That is the safe direction — the
 * feature simply doesn't run, exactly as before it existed. If production is ever served
 * from another origin (the `.firebaseapp.com` alias, or a custom domain), add it to
 * `PRODUCTION_HOSTNAMES` or the warm-up will be silently inert there.
 */
export const IS_PRODUCTION_ORIGIN: boolean =
  IS_PRODUCTION &&
  typeof window !== 'undefined' &&
  PRODUCTION_HOSTNAMES.has(window.location.hostname);

/**
 * Base URL prefix for the notification SSE stream.
 * Dev: uses the Vite proxy alias so auth cookies + headers flow through localhost.
 * Prod: bypasses Firebase Hosting (CDN buffers text/event-stream) by calling
 * the platform-gateway Cloud Run service directly.
 */
export const notificationsSseBaseUrl: string = import.meta.env.DEV
  ? '/notifications-api'
  : `${ENV.VITE_PLATFORM_BASE_URL}/api/v1/notification`;

/**
 * Base URL prefix for the enriched-product export SSE status stream
 * (`/api/v1/export/v1/exports/{id}/status/stream`). Moved off workflow-webapp
 * with the rest of the lifecycle — the export row belongs to whichever service
 * handled the trigger, so the stream must read from the same one.
 * Dev: uses the experiment-service Vite proxy alias so auth headers flow through.
 * Prod: bypasses Firebase Hosting (its CDN buffers `text/event-stream`) by
 * calling the platform-gateway Cloud Run service directly — same rationale as
 * `notificationsSseBaseUrl`.
 */
export const exportsSseBaseUrl: string = import.meta.env.DEV
  ? '/export-api'
  : `${ENV.VITE_PLATFORM_BASE_URL}/api/v1/export`;

/**
 * Base URL prefix for the integration-service delivery-run SSE stream
 * (`/api/v1/integration/deliveries/runs/{runId}/stream`).
 * Dev: the integrations Vite proxy alias so auth headers flow through.
 * Prod: bypasses Firebase Hosting (its CDN buffers `text/event-stream`) by
 * calling the platform-gateway Cloud Run service directly — same rationale as
 * `notificationsSseBaseUrl`. Mirrors `integrationsPrefixUrl` in `~/api/client.ts`.
 */
export const integrationsSseBaseUrl: string = import.meta.env.DEV
  ? '/integrations-api'
  : `${ENV.VITE_PLATFORM_BASE_URL}/api/v1/integration`;

/**
 * Base URL prefix for the ai-mcp-server chat / copilot SSE stream.
 * Dev: Vite proxy alias `/mcp-api` → `VITE_MCP_BASE_URL`.
 * Prod: Firebase Hosting rewrite `/mcp-api/**` → ai-mcp-server Cloud Run
 * service (defined in `firebase.json`). Same direct-Cloud-Run pattern as
 * notifications SSE — Firebase Hosting buffers `text/event-stream` through
 * `run` rewrites so streamed responses get stitched into one payload.
 */
export const mcpApiBaseUrl: string = import.meta.env.DEV ? '/mcp-api' : ENV.VITE_MCP_BASE_URL;

/**
 * Temporal Web UI base URL per environment, keyed off the Vite build `mode`
 * (`.env.develop` / `.env.staging` / `.env.production`). The local `vite dev`
 * server runs in mode `development`, which falls through to the dev cluster.
 * Not sourced from a `VITE_*` var — these hosts are fixed infra, not per-deploy
 * config.
 */
const TEMPORAL_DEV_URL = 'https://temporal-dev.lily.ai';
const TEMPORAL_BASE_URLS: Record<string, string> = {
  production: 'https://temporal-prod.lily.ai',
  staging: 'https://temporal-staging.lily.ai',
  develop: TEMPORAL_DEV_URL,
};
export const temporalBaseUrl: string = TEMPORAL_BASE_URLS[import.meta.env.MODE] ?? TEMPORAL_DEV_URL;

/**
 * Deployed front-end origin the SSO dev-bridge hands OAuth initiation to, keyed
 * off the Vite build `mode` exactly like `temporalBaseUrl` above — `vite dev`
 * runs in mode `development`, which falls through to the dev site. These are the
 * Firebase Hosting sites from `.firebaserc` (fixed infra, not per-deploy config);
 * dev is addressed by its `agentic-dev.lily.ai` custom domain
 * — NOT the API host: `/login` is an SPA route, so pointing the bridge at
 * `VITE_API_BASE_URL` (the identity-service host) 404s. The bridge only fires in
 * dev, so in practice only the dev site is ever used.
 */
const BRIDGE_FRONTEND_DEV_URL = 'https://agentic-dev.lily.ai';
const BRIDGE_FRONTEND_URLS: Record<string, string> = {
  production: 'https://lily-agentic.web.app',
  staging: 'https://lily-agentic-staging.web.app',
  develop: BRIDGE_FRONTEND_DEV_URL,
};
export const bridgeFrontendUrl: string =
  BRIDGE_FRONTEND_URLS[import.meta.env.MODE] ?? BRIDGE_FRONTEND_DEV_URL;

/**
 * Langfuse (LLM observability) host and project slug per environment, keyed off
 * the Vite build `mode` exactly like `temporalBaseUrl` above — the local
 * `vite dev` server runs in mode `development`, which falls through to dev. Also
 * fixed infra rather than per-deploy config, so no `VITE_*` var.
 *
 * Host and project slug always move together (`langfuse-dev` ⇄ `lily-dev`), so
 * they live in one entry rather than two parallel maps that could drift.
 */
interface LangfuseEnvironment {
  baseUrl: string;
  /** Langfuse project slug — the `/project/<slug>/` path segment. */
  project: string;
}

const LANGFUSE_DEV: LangfuseEnvironment = {
  baseUrl: 'https://langfuse-dev.lily.ai',
  project: 'lily-dev',
};

const LANGFUSE_ENVIRONMENTS: Record<string, LangfuseEnvironment> = {
  production: { baseUrl: 'https://langfuse-prod.lily.ai', project: 'lily-prod' },
  staging: { baseUrl: 'https://langfuse-staging.lily.ai', project: 'lily-staging' },
  develop: LANGFUSE_DEV,
};

const langfuseEnvironment: LangfuseEnvironment =
  LANGFUSE_ENVIRONMENTS[import.meta.env.MODE] ?? LANGFUSE_DEV;

/**
 * Langfuse "user" page for one Lily product id — the traces Langfuse recorded
 * against that product. Returns `null` for a blank id so a caller renders no
 * link rather than one pointing at an empty user page.
 *
 * The id is percent-encoded on principle; Lily ids are UUIDs today, which need
 * no encoding, but nothing here depends on that staying true.
 */
export function langfuseUserUrl(lilyId: string | null | undefined): string | null {
  if (!lilyId || lilyId.trim() === '') return null;
  const { baseUrl, project } = langfuseEnvironment;
  return `${baseUrl}/project/${project}/users/${encodeURIComponent(lilyId.trim())}`;
}

/**
 * Temporal namespaces workflows run under. Most integration-service work
 * (schedules, delivery, analytics sync) lives in `agentic-integrations`; the
 * catalog-ops / feed-audit batch pipeline runs in `agentic-workflow`.
 * `integrations` stays the default everywhere for backward compatibility.
 */
export const TEMPORAL_NAMESPACE = {
  integrations: 'agentic-integrations',
  workflow: 'agentic-workflow',
} as const;

/** A valid Temporal namespace value (one of {@link TEMPORAL_NAMESPACE}). */
export type TemporalNamespace = (typeof TEMPORAL_NAMESPACE)[keyof typeof TEMPORAL_NAMESPACE];

/**
 * Builds `<base>/namespaces/<ns>/<segment>/<id>`. Namespace defaults to
 * `agentic-integrations` so existing callers are unaffected. Ids are
 * percent-encoded because workflow ids here embed colons and ISO timestamps
 * (e.g. `google-ads-schedule-analytics-…-2026-07-29T00:00:00Z`).
 */
function temporalUrl(
  segment: 'schedules' | 'workflows',
  id: string,
  namespace: string = TEMPORAL_NAMESPACE.integrations,
): string {
  return `${temporalBaseUrl}/namespaces/${namespace}/${segment}/${encodeURIComponent(id)}`;
}

/**
 * Deep link to a specific workflow in the Temporal Web UI for the current
 * environment, e.g. `https://temporal-prod.lily.ai/namespaces/agentic-integrations/workflows/<id>`.
 * `namespace` defaults to `agentic-integrations`; pass `TEMPORAL_NAMESPACE.workflow`
 * for batch-pipeline workflows. Returns null when there's no workflow id, so
 * callers can skip rendering a link.
 */
export function temporalWorkflowUrl(
  workflowId: string | null | undefined,
  namespace: string = TEMPORAL_NAMESPACE.integrations,
): string | null {
  if (!workflowId) return null;
  return temporalUrl('workflows', workflowId, namespace);
}

/**
 * Deep link to a Temporal *schedule* (not a workflow run) — the recurring
 * definition the Scheduled Jobs page manages. Null on a missing id, matching
 * `temporalWorkflowUrl`, so callers share one "no link" branch.
 */
export function temporalScheduleUrl(scheduleId: string | null | undefined): string | null {
  if (!scheduleId) return null;
  return temporalUrl('schedules', scheduleId);
}
